import { useState } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Ticket {
  id: string
  ticket_number: string
  user_id: string
  user_email?: string
  user_name?: string
  user_plan?: string
  subject: string
  message: string
  status: string
  state: string
  priority: string
  urgency: string
  impact: string
  category: string
  subcategory?: string
  channel: string
  contact_method: string
  assigned_group?: string
  assigned_to?: string
  admin_reply?: string
  work_notes?: string
  resolution_notes?: string
  configuration_item?: string
  correlation_id?: string
  time_worked_seconds: number
  resolved_at?: string
  closed_at?: string
  created_at: string
  updated_at: string
}

export function TicketModal({ ticket, onClose, onUpdate }: { ticket: Ticket; onClose: () => void; onUpdate: () => void }) {
  const [form, setForm] = useState({ ...ticket })
  const [newWorkNote, setNewWorkNote] = useState('')
  const [newComment, setNewComment] = useState('')
  const [saving, setSaving] = useState(false)

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }))

  const save = async () => {
    setSaving(true)
    await authFetch(apiUrl(`/api/enterprise/tickets/${ticket.id}`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        status: form.status,
        priority: form.priority,
        urgency: form.urgency,
        impact: form.impact,
        state: form.state,
        assigned_group: form.assigned_group,
        assigned_to: form.assigned_to,
        admin_reply: newComment || form.admin_reply,
        work_notes: newWorkNote ? (form.work_notes ? form.work_notes + '\n---\n' + newWorkNote : newWorkNote) : form.work_notes,
        resolution_notes: form.resolution_notes,
        configuration_item: form.configuration_item,
      }),
    }).catch(() => {})
    setSaving(false)
    onUpdate()
    onClose()
  }

  const timeWorked = () => {
    const s = form.time_worked_seconds || 0
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  const timeSinceCreated = () => {
    const diff = Date.now() - new Date(ticket.created_at).getTime()
    const hours = Math.floor(diff / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    return hours > 24 ? `${Math.floor(hours / 24)}d ${hours % 24}h` : `${hours}h ${mins}m`
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.ticketNum}>{ticket.ticket_number || 'TKT-???'}</span>
          <span style={styles.headerTitle}>{ticket.subject}</span>
          <div style={{ flex: 1 }} />
          <span style={{ ...styles.stateBadge, background: form.state === 'new' ? 'rgba(245,161,87,0.15)' : form.state === 'in_progress' ? 'rgba(56,189,248,0.15)' : form.state === 'resolved' ? 'rgba(95,212,164,0.15)' : 'rgba(255,255,255,0.05)', color: form.state === 'new' ? '#f5a157' : form.state === 'in_progress' ? '#38bdf8' : form.state === 'resolved' ? '#5fd4a4' : '#666' }}>{form.state}</span>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>

        <div style={styles.body}>
          {/* Left column - Form fields */}
          <div style={styles.leftCol}>
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Caller Information</h4>
              <Field label="Number" value={ticket.ticket_number || ''} readonly />
              <Field label="Caller" value={ticket.user_email || ''} readonly />
              <Field label="Plan" value={ticket.user_plan || 'free'} readonly />
              <Field label="Contact Method" value={form.contact_method} onChange={v => update('contact_method', v)} options={['email', 'chat', 'phone']} />
              <Field label="Channel" value={form.channel} onChange={v => update('channel', v)} options={['web', 'email', 'api', 'chat']} />
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Incident Details</h4>
              <Field label="Category" value={form.category} onChange={v => update('category', v)} options={['general', 'billing', 'ai', 'deployment', 'bug', 'feature_request', 'account', 'performance']} />
              <Field label="Subcategory" value={form.subcategory || ''} onChange={v => update('subcategory', v)} />
              <Field label="Configuration Item" value={form.configuration_item || ''} onChange={v => update('configuration_item', v)} />
              <Field label="Correlation ID" value={form.correlation_id || ''} readonly />
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Priority & Assignment</h4>
              <Field label="State" value={form.state} onChange={v => update('state', v)} options={['new', 'in_progress', 'on_hold', 'resolved', 'closed', 'cancelled']} />
              <Field label="Impact" value={form.impact} onChange={v => update('impact', v)} options={['low', 'medium', 'high', 'critical']} />
              <Field label="Urgency" value={form.urgency} onChange={v => update('urgency', v)} options={['low', 'medium', 'high', 'critical']} />
              <Field label="Priority" value={form.priority} onChange={v => update('priority', v)} options={['low', 'normal', 'high', 'urgent', 'critical']} />
              <Field label="Assignment Group" value={form.assigned_group || ''} onChange={v => update('assigned_group', v)} options={['', 'engineering', 'support', 'billing', 'devops']} />
              <Field label="Assigned To" value={form.assigned_to || ''} onChange={v => update('assigned_to', v)} />
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Timing</h4>
              <Field label="Time Worked" value={timeWorked()} readonly />
              <Field label="Age" value={timeSinceCreated()} readonly />
              <Field label="Created" value={new Date(ticket.created_at).toLocaleString()} readonly />
              {ticket.resolved_at && <Field label="Resolved" value={new Date(ticket.resolved_at).toLocaleString()} readonly />}
              {ticket.closed_at && <Field label="Closed" value={new Date(ticket.closed_at).toLocaleString()} readonly />}
            </div>
          </div>

          {/* Right column - Description, Notes, Activities */}
          <div style={styles.rightCol}>
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Short Description</h4>
              <div style={styles.descBox}>{ticket.subject}</div>
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Description</h4>
              <div style={styles.descBox}>{ticket.message}</div>
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Resolution Notes</h4>
              <textarea value={form.resolution_notes || ''} onChange={e => update('resolution_notes', e.target.value)} placeholder="Resolution details..." style={styles.textarea} rows={3} />
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Work Notes (Internal)</h4>
              {form.work_notes && <div style={styles.notesBox}>{form.work_notes}</div>}
              <textarea value={newWorkNote} onChange={e => setNewWorkNote(e.target.value)} placeholder="Add internal work note..." style={styles.textarea} rows={2} />
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Customer Reply</h4>
              {form.admin_reply && <div style={styles.replyBox}>{form.admin_reply}</div>}
              <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Reply to customer (visible to them)..." style={styles.textarea} rows={2} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={save} disabled={saving} style={styles.saveBtn}>{saving ? 'Saving...' : 'Save & Update'}</button>
          <button onClick={() => { update('state', 'resolved'); setTimeout(save, 100) }} style={styles.resolveBtn}>Resolve</button>
          <button onClick={() => { update('state', 'closed'); setTimeout(save, 100) }} style={styles.closeTicketBtn}>Close Ticket</button>
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, onChange, options, readonly }: { label: string; value: string; onChange?: (v: string) => void; options?: string[]; readonly?: boolean }) {
  return (
    <div style={styles.field}>
      <label style={styles.fieldLabel}>{label}</label>
      {readonly ? (
        <span style={styles.fieldValue}>{value}</span>
      ) : options ? (
        <select value={value} onChange={e => onChange?.(e.target.value)} style={styles.fieldSelect}>
          {options.map(o => <option key={o} value={o} style={{ background: '#1a1a2e', color: '#e0e0e0' }}>{o || '(none)'}</option>)}
        </select>
      ) : (
        <input value={value} onChange={e => onChange?.(e.target.value)} style={styles.fieldInput} />
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: '90vw', maxWidth: 1100, height: '85vh', maxHeight: 750, background: '#0f1119', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#161825' },
  ticketNum: { fontSize: 11, fontWeight: 700, color: '#7c6ef5', fontFamily: 'ui-monospace, monospace', padding: '2px 8px', background: 'rgba(124,110,245,0.1)', borderRadius: 4 },
  headerTitle: { fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stateBadge: { padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700, fontFamily: 'ui-monospace, monospace' },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', padding: '0 4px' },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  leftCol: { width: 340, borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'auto', padding: '12px 14px' },
  rightCol: { flex: 1, overflow: 'auto', padding: '12px 14px' },
  section: { marginBottom: 16 },
  sectionTitle: { fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.05em' },
  field: { display: 'flex', alignItems: 'center', marginBottom: 6 },
  fieldLabel: { width: 120, fontSize: 10, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace', flexShrink: 0 },
  fieldValue: { fontSize: 11, color: '#e0e0e0', fontFamily: 'ui-monospace, monospace' },
  fieldInput: { flex: 1, padding: '4px 8px', borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 10, fontFamily: 'ui-monospace, monospace', outline: 'none' },
  fieldSelect: { flex: 1, padding: '4px 8px', borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 10, fontFamily: 'ui-monospace, monospace', cursor: 'pointer' },
  descBox: { padding: '8px 10px', borderRadius: 4, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const },
  textarea: { width: '100%', padding: '8px 10px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: '#1a1a2e', color: '#e0e0e0', fontSize: 11, fontFamily: 'ui-monospace, monospace', outline: 'none', resize: 'vertical' as const },
  notesBox: { padding: '8px 10px', borderRadius: 4, background: 'rgba(245,161,87,0.04)', border: '1px solid rgba(245,161,87,0.15)', fontSize: 10, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const, marginBottom: 6 },
  replyBox: { padding: '8px 10px', borderRadius: 4, background: 'rgba(124,110,245,0.04)', border: '1px solid rgba(124,110,245,0.15)', fontSize: 10, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const, marginBottom: 6 },
  footer: { display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', background: '#161825' },
  saveBtn: { padding: '6px 16px', borderRadius: 4, border: 'none', background: '#7c6ef5', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  resolveBtn: { padding: '6px 16px', borderRadius: 4, border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)', color: '#5fd4a4', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  closeTicketBtn: { padding: '6px 16px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.5)', fontSize: 11, cursor: 'pointer' },
  cancelBtn: { padding: '6px 16px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', marginLeft: 'auto' },
}
